const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function parseTimeToMinutes(time: string): number {
  if (!TIME_RE.test(time)) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function formatMinutesAsTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function generateSlots(
  openTime: string,
  closeTime: string,
  slotDurationMinutes: number,
): string[] {
  const start = parseTimeToMinutes(openTime);
  const end = parseTimeToMinutes(closeTime);
  if (slotDurationMinutes <= 0 || end <= start) return [];

  const slots: string[] = [];
  for (let t = start; t + slotDurationMinutes <= end; t += slotDurationMinutes) {
    slots.push(`${formatMinutesAsTime(t)}-${formatMinutesAsTime(t + slotDurationMinutes)}`);
  }
  return slots;
}

export function isDayOpen(dateStr: string, openDays: number[]): boolean {
  if (!dateStr) return true;
  const day = new Date(`${dateStr}T12:00:00`).getDay();
  return openDays.includes(day);
}

export function formatSlotDuration(minutes: number): string {
  return `${minutes} min`;
}

/** Display court time like 06:00 → 6am, 23:00 → 11pm */
export function formatTime12Hour(time: string): string {
  if (!TIME_RE.test(time)) return time;
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  if (m === 0) return `${hour12}${period}`;
  return `${hour12}:${String(m).padStart(2, '0')}${period}`;
}

/** Earliest open and latest close across enabled courts, e.g. 6am–11pm */
export function getAggregatedOperatingHours(
  settings: Array<{ enabled: boolean; openTime: string; closeTime: string }>,
): string | null {
  const active = settings.filter((s) => s.enabled);
  if (active.length === 0) return null;

  let earliest = Infinity;
  let latest = -1;

  for (const s of active) {
    const open = parseTimeToMinutes(s.openTime);
    const close = parseTimeToMinutes(s.closeTime);
    if (close <= open) continue;
    earliest = Math.min(earliest, open);
    latest = Math.max(latest, close);
  }

  if (!Number.isFinite(earliest) || latest < 0) return null;

  return `${formatTime12Hour(formatMinutesAsTime(earliest))}–${formatTime12Hour(formatMinutesAsTime(latest))}`;
}

export const CANCELLATION_CUTOFF_OPTIONS = [
  { value: 30, label: '30 minutes before slot' },
  { value: 60, label: '1 hour before slot' },
  { value: 120, label: '2 hours before slot' },
  { value: 240, label: '4 hours before slot' },
  { value: 360, label: '6 hours before slot' },
  { value: 720, label: '12 hours before slot' },
  { value: 1440, label: '24 hours before slot' },
  { value: 2880, label: '48 hours before slot' },
] as const;

export function formatCancellationCutoff(minutes: number): string {
  const match = CANCELLATION_CUTOFF_OPTIONS.find((o) => o.value === minutes);
  if (match) return match.label;
  if (minutes < 60) return `${minutes} minutes before slot`;
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'} before slot`;
  }
  return `${minutes} minutes before slot`;
}
